import {
  faSortAlphaDown,
  faSortAlphaUp,
} from '@fortawesome/pro-duotone-svg-icons';
import { faEllipsisH } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from 'src/common/components/Modal';
import { SelectSitelife } from 'src/common/components/SelectSitelife';
import { SelectSitelifeOptionType } from 'src/common/components/SelectSitelife/types';
import { ActionIcon } from 'src/common/components/buttons/ActionIcon';
import { SelectNative } from 'src/common/components/inputs/SelectNative/SelectNative';
import { TextInput } from 'src/common/components/inputs/TextInput/TextInput';
import {
  ProjectFilter,
  ProjectArchiveStatus,
} from 'src/project/types/projects';
import { getProjectFilterLabel, getProjectArchiveStatusLabel } from '../utils';

type Props = {
  search: string;
  onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  typeFilterOptions: SelectSitelifeOptionType[];
  statusFilterOptions: SelectSitelifeOptionType[];
  selectedTypeFilters: ProjectFilter[];
  selectedStatusFilters: ProjectArchiveStatus[];
  onTypeFilterChange: (filters: ProjectFilter[]) => void;
  onStatusFilterChange: (filters: ProjectArchiveStatus[]) => void;
  sortProperty: 'name' | 'createdAt';
  onSortPropertyChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  sortDirection: 'asc' | 'desc';
  onToggleSortDirection: () => void;
  isMobileFiltersModalOpen: boolean;
  onOpenMobileFiltersModal: () => void;
  onCloseMobileFiltersModal: () => void;
};

export const ProjectListActionBar = ({
  search,
  onSearchChange,
  typeFilterOptions,
  statusFilterOptions,
  selectedTypeFilters,
  selectedStatusFilters,
  onTypeFilterChange,
  onStatusFilterChange,
  sortProperty,
  onSortPropertyChange,
  sortDirection,
  onToggleSortDirection,
  isMobileFiltersModalOpen,
  onOpenMobileFiltersModal,
  onCloseMobileFiltersModal,
}: Props) => {
  const { t } = useTranslation(['common', 'project']);

  return (
    <div className="w-full">
      <h1 className="text-xl font-semibold">{t('project:listView.title')}</h1>
      <div className="mt-2 flex flex-col gap-3 md:flex-row md:items-end md:gap-4">
        <div className="flex items-end gap-2">
          <TextInput
            placeholder={t('project:listView.actionBar.searchPlaceholder')}
            className="w-full md:w-[200px]"
            value={search}
            onChange={onSearchChange}
            aria-label={t('project:listView.actionBar.searchPlaceholder')}
          />
          <ActionIcon
            className="mb-1 shrink-0 md:hidden"
            onClick={onOpenMobileFiltersModal}
            aria-label={t('common:filterBy')}
          >
            <FontAwesomeIcon icon={faEllipsisH} size="lg" />
          </ActionIcon>
        </div>
        <div className="hidden min-w-0 flex-col gap-3 md:flex md:flex-row md:items-end md:gap-2">
          <div className="flex min-w-0 flex-col border-l-2 pl-4">
            <span>{t('common:filterByType')}</span>
            <SelectSitelife
              className="mt-1 w-full md:mr-2 md:w-[274px]"
              options={typeFilterOptions}
              value={selectedTypeFilters.map((f) => ({
                label: getProjectFilterLabel(f, t),
                value: f,
              }))}
              onChange={(selection: SelectSitelifeOptionType[]) => {
                onTypeFilterChange(
                  selection.map((p) => p.value as ProjectFilter),
                );
              }}
              isMulti
              closeMenuOnSelect={false}
            />
          </div>
          <div className="flex min-w-0 flex-col">
            <span>{t('common:filterByStatus')}</span>
            <SelectSitelife
              className="mt-1 w-full md:mr-2 md:w-[274px]"
              options={statusFilterOptions}
              value={selectedStatusFilters.map((f) => ({
                label: getProjectArchiveStatusLabel(f, t),
                value: f,
              }))}
              onChange={(selection: SelectSitelifeOptionType[]) => {
                onStatusFilterChange(
                  selection.map((p) => p.value as ProjectArchiveStatus),
                );
              }}
              isMulti
              closeMenuOnSelect={false}
            />
          </div>
          <div className="flex items-end gap-2">
            <div className="flex min-w-0 flex-1 flex-col border-l-2 pl-4 md:flex-none">
              <span>{t('common:sortBy')}</span>
              <SelectNative
                onChange={onSortPropertyChange}
                value={sortProperty}
                aria-label="sort"
                className="mt-1 w-full"
              >
                <option value="name">
                  {t('project:listView.actionBar.projectName')}
                </option>
                <option value="createdAt">
                  {t('project:listView.actionBar.creationDate')}
                </option>
              </SelectNative>
            </div>
            <ActionIcon
              className="mb-1 shrink-0"
              onClick={onToggleSortDirection}
              aria-label="toggle sort direction"
            >
              <FontAwesomeIcon
                icon={sortDirection === 'asc' ? faSortAlphaDown : faSortAlphaUp}
                size="lg"
              />
            </ActionIcon>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isMobileFiltersModalOpen}
        onRequestClose={onCloseMobileFiltersModal}
        className="w-full max-w-[560px] md:hidden"
      >
        <Modal.Header onClose={onCloseMobileFiltersModal}>
          {t('common:filterBy')}
        </Modal.Header>
        <Modal.Body className="space-y-4">
          <div className="flex flex-col">
            <span>{t('common:filterByType')}</span>
            <SelectSitelife
              className="mt-1 w-full"
              options={typeFilterOptions}
              value={selectedTypeFilters.map((f) => ({
                label: getProjectFilterLabel(f, t),
                value: f,
              }))}
              onChange={(selection: SelectSitelifeOptionType[]) => {
                onTypeFilterChange(
                  selection.map((p) => p.value as ProjectFilter),
                );
              }}
              isMulti
              closeMenuOnSelect={false}
              usePortaling
            />
          </div>
          <div className="flex flex-col">
            <span>{t('common:filterByStatus')}</span>
            <SelectSitelife
              className="mt-1 w-full"
              options={statusFilterOptions}
              value={selectedStatusFilters.map((f) => ({
                label: getProjectArchiveStatusLabel(f, t),
                value: f,
              }))}
              onChange={(selection: SelectSitelifeOptionType[]) => {
                onStatusFilterChange(
                  selection.map((p) => p.value as ProjectArchiveStatus),
                );
              }}
              isMulti
              closeMenuOnSelect={false}
              usePortaling
            />
          </div>
          <div className="flex items-end gap-2">
            <div className="flex min-w-0 flex-1 flex-col">
              <span>{t('common:sortBy')}</span>
              <SelectNative
                onChange={onSortPropertyChange}
                value={sortProperty}
                aria-label="sort"
                className="mt-1 w-full"
              >
                <option value="name">
                  {t('project:listView.actionBar.projectName')}
                </option>
                <option value="createdAt">
                  {t('project:listView.actionBar.creationDate')}
                </option>
              </SelectNative>
            </div>
            <ActionIcon
              className="mb-1 shrink-0"
              onClick={onToggleSortDirection}
              aria-label="toggle sort direction"
            >
              <FontAwesomeIcon
                icon={sortDirection === 'asc' ? faSortAlphaDown : faSortAlphaUp}
                size="lg"
              />
            </ActionIcon>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};
